<template>
  <div class="sales page page-with-table p-3">
    <div class="card mb-3 p-3">
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <h4 class="mb-0 mr-4">
            {{ $t('Batch Lot Up for Sale') }}
            <router-link v-if="sale" :to="{ name: 'sales.lots', params: { id: sale.uuid }}">
              {{ sale.sale_no }}
            </router-link>
            <live-sale-ref v-if="sale && sale.live_reference" :href="sale.live_url" :label="sale.live_reference"/>
          </h4>
        </div>
        <div class="d-flex align-items-center">
          <h5 class="mb-0 d-inline-block mr-2">{{ $t('Group By') }}</h5>
          <b-form-group class="mb-0 d-inline-block">
            <b-form-radio-group @change="onGroupByChange($event)" v-model="currentGroupBy" :options="options" buttons
                                button-variant="outline-primary" :disabled="isInProgress"/>
          </b-form-group>
        </div>
        <div class="d-flex align-items-center">
          <h5 class="mb-0 d-inline-block mr-2">{{ $t('View') }}</h5>
          <b-form-group class="mb-0 d-inline-block">
            <b-form-radio-group v-model="currentView" buttons
                                button-variant="outline-primary">
              <template v-for="option in viewOptions">
                <b-form-radio :value="option.value" :key="option.value">
                  <b-tooltip :target="option.value" :title="option.text" :key="`${option.value}_tooltip`"></b-tooltip>
                  <div class="tooltip-shower" :id="option.value"></div>
                  <i class="fa fa-th" v-if="option.value === 'thumbnail'" aria-hidden="true"></i>
                  <i class="fa fa-list" v-else aria-hidden="true"></i>
                </b-form-radio>
              </template>
            </b-form-radio-group>
          </b-form-group>
        </div>
        <div class="d-flex align-items-center">
          <submit-button :show-loader="isInProgress || disableSubmit" class="ml-auto" variant="primary"
                         @click="onSaveClick">
            {{ $t('Save') }}
          </submit-button>
        </div>
      </div>
    </div>

    <div class="content-wrapper">
      <spinner :show="loading"/>
      <div v-if="!loading" class="h-100">
        <div class="flex-container flex-container-style fixed-height h-100">
          <div class="flex-item d-flex h-100">
            <div class="card w-100">
              <div class="card-header">
                <h5 class="m-0">
                  {{ $t('Reserved Items') }}
                </h5>
              </div>
              <div class="card-body outer-card-body">
                <div class="card" v-for="(outerData, outerIndex) in reservedItemsMap"
                     :key="outerData.name">
                  <div class="card-header bg-secondary pt-1 pb-1">
                    <div class="row">
                      <div class="col-sm-12 d-flex align-items-center ">
                        <a href="javascript:void(0)" v-b-toggle="`${outerData.name}-items-toggle-${outerIndex}`"
                           class="mr-2 text-black-50">
                            <span class="when-opened">
                              <font-awesome-icon class="icon" icon="chevron-down"/>
                            </span>
                          <span class="when-closed">
                              <font-awesome-icon class="icon" icon="chevron-right"/>
                            </span>
                        </a>
                        <b-checkbox :id="`${outerData.name}-items-checkbox-${outerIndex}`"
                                    class="d-inline-block"
                                    @change="selectAllRows($event, outerData)"
                                    v-model="outerData.checked"/>
                        <h5 class="d-inline-block mb-0 ml-2">
                          <label class="m-0" :for="`${outerData.name}-items-checkbox-${outerIndex}`">
                            {{ outerData.name }}
                          </label>
                        </h5>
                      </div>
                    </div>
                  </div>
                  <b-collapse :id="`${outerData.name}-items-toggle-${outerIndex}`" role="tabpanel" visible>
                    <div class="card-body inner-items-card-body p-0">
                      <table class="table table-sm mb-0">
                        <thead>
                        <tr>
                          <th style="width: 4.5em"></th>
                          <th class="stock-th">{{ $t('Stock') }}</th>
                          <th v-if="thumbnailView" class="w-25">{{ $t('Image') }}</th>
                          <th>{{ $t('Title') }}</th>
                          <th v-if="enableInventoryQuantity" class="quantity-th">{{ $t('Quantity') }}</th>
                          <th v-if="thumbnailView" class="estimate-th">{{ $t('Estimate') }}</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(item, index) in outerData.items" :key="`item-row-${index}`"
                            :style="{background: getBgColor(item)}">
                          <td class="text-right">
                            <b-checkbox @change="selectRow($event, item, outerData)" class="d-inline-block"
                                        v-model="item.checked"/>
                          </td>
                          <td>{{ item.inventory.receipt.receipt_no }}/{{ item.inventory.item_no }}</td>
                          <batch-lot-up-image-td v-if="thumbnailView" :item="item"
                                                 @onGalleryClick="showImageGallery"/>
                          <td class="title">{{ item.inventory.short_description | striptags | truncate(50) }}</td>
                          <td v-if="enableInventoryQuantity">{{ item.quantity }}</td>
                          <td v-if="thumbnailView">{{ item.low | asCurrency(sale.currency.symbol) }} - {{
                              item.high | asCurrency(sale.currency.symbol)
                            }}
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </b-collapse>
                </div>
              </div>
            </div>
          </div>
          <div class="flex-item text-center" style="width: 70px">
            <b-button :disabled="isInProgress" variant="primary" @click="onMoveRightClick" class="mb-2"
                      style="width: 9em">
              <font-awesome-icon class="icon mr-2" icon="angle-double-right"/>
              <font-awesome-icon class="icon" icon="angle-double-right"/>
            </b-button>
            <br>
            <b-button :disabled="isInProgress" variant="primary" @click="onMoveLeftClick" style="width: 9em">
              <font-awesome-icon class="icon mr-2" icon="angle-double-left"/>
              <font-awesome-icon class="icon" icon="angle-double-left"/>
            </b-button>
          </div>
          <div class="flex-item d-flex h-100">
            <div class="card w-100">
              <div class="card-header d-flex justify-content-between">
                <h5 class="mb-0">
                  {{ $t('Lots') }}
                </h5>
                <div class="d-flex align-items-center">
                  <div class="mr-2">{{
                      !blockingEnabled ? $t('Lock Lotted and Published Items') : $t('Unlock Lotted and Published Items')
                    }}
                  </div>
                  <b-button size="sm"
                            style="min-width: 30px"
                            :variant="blockingEnabled ? 'primary' : 'outline-primary' "
                            @click="blockingEnabled=!blockingEnabled"
                            id="block-lot-nos">
                    <font-awesome-icon v-if="blockingEnabled" icon="lock" class="icon"/>
                    <font-awesome-icon v-else icon="lock-open" class="icon"/>
                  </b-button>
                </div>
              </div>
              <div class="card-body outer-card-body">
                <draggable handle=".handle" class="list-group" :list="lotItemsMap"
                           @change="onOuterDataDrag" :options="{'disabled': isInProgress}"
                           group="people">
                  <div class="card" v-for="(outerData, outerIndex) in lotItemsMap"
                       :key="outerData.name">
                    <div class="card-header bg-secondary pt-1 pb-1">
                      <div class="row">
                        <div class="col-sm-8 d-flex align-items-center ">
                          <a href="javascript:void(0)" v-b-toggle="`${outerData.name}-lots-toggle-${outerIndex}`"
                             class="mr-2 text-black-50">
                            <span class="when-opened">
                              <font-awesome-icon class="icon" icon="chevron-down"/>
                            </span>
                            <span class="when-closed">
                              <font-awesome-icon class="icon" icon="chevron-right"/>
                            </span>
                          </a>
                          <b-checkbox :id="`${outerData.name}-lots-checkbox-${outerIndex}`"
                                      class="d-inline-block"
                                      @change="selectAllRows($event, outerData)"
                                      v-model="outerData.checked"/>
                          <h5 class="d-inline-block mb-0 ml-2">
                            <label class="m-0" :for="`${outerData.name}-lots-checkbox-${outerIndex}`">
                              {{ outerData.name }}
                            </label>
                          </h5>
                        </div>
                        <div class="col-sm-4 d-flex align-items-center justify-content-end">
                          <font-awesome-icon style="font-size: 1.2em" class="icon handle hover-row-resize" icon="bars"/>
                        </div>
                      </div>
                    </div>
                    <b-collapse :id="`${outerData.name}-lots-toggle-${outerIndex}`" role="tabpanel" visible>
                      <div class="card-body inner-items-card-body p-0">
                        <table class="table table-sm mb-0">
                          <thead>
                          <tr>
                            <th class="handler-th"></th>
                            <th class="checkbox-th"></th>
                            <th class="stock-th stock-sort" @click="sortByStockNo(outerData)">
                              {{ $t('Stock') }}
                              <sort-icon :sort-state="outerData.orderDesc"/>
                            </th>
                            <th class="lot-th">{{ $t('Lot') }}</th>
                            <th v-if="thumbnailView" class="image-th w-25">{{ $t('Image') }}</th>
                            <th class="title-th">{{ $t('Title') }}</th>
                            <th v-if="enableInventoryQuantity" class="quantity-th">{{ $t('Quantity') }}</th>
                            <th v-if="thumbnailView" class="estimate-th">{{ $t('Estimate') }}</th>
                          </tr>
                          </thead>
                          <draggable :options="{'disabled': isInProgress}" @change="onInnerItemDrag"
                                     :list="outerData.items" tag="tbody" handle=".handle">
                            <tr v-for="(item, index) in outerData.items" :key="`inner-item-row-${index}`"
                                :style="{background: getBgColor(item)}">
                              <td class="text-right">
                                <font-awesome-icon class="icon handle hover-row-resize" icon="bars"/>
                              </td>
                              <td class="pl-2">
                                <b-checkbox @change="selectRow($event, item, outerData)" class="d-inline-block"
                                            v-model="item.checked"
                                            :disabled="!!(blockingEnabled && item.lot && item.lot.live_id)"/>
                              </td>
                              <td>{{ item.inventory.receipt.receipt_no }}/{{ item.inventory.item_no }}</td>
                              <td>{{ item.lot_no }}</td>
                              <batch-lot-up-image-td v-if="thumbnailView" :item="item"
                                                     @onGalleryClick="showImageGallery"/>
                              <td class="title">{{ item.inventory.short_description | striptags | truncate(50) }}</td>
                              <td v-if="enableInventoryQuantity">{{ item.quantity }}</td>
                              <td v-if="thumbnailView">{{ item.low | asCurrency(sale.currency.symbol) }} -
                                {{ item.high | asCurrency(sale.currency.symbol) }}
                              </td>
                            </tr>
                          </draggable>
                        </table>
                      </div>
                    </b-collapse>
                  </div>
                </draggable>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <image-viewer ref="imageViewer"/>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import Spinner from "../../../core/components/common/Spinner";
  import draggable from 'vuedraggable'
  import _ from 'lodash';
  import SalesService from "./../SalesService";
  import ImageViewer from "@/core/components/image-viewer/ImageViewer";
  import BatchLotUpImageTd from "@/modules/sales/batch-lot-up/BatchLotUpImageTd";
  import LiveSaleRef from "@/core/components/common/LiveSaleRef";
  import SortIcon from "@/core/components/sort-icon/SortIcon";
  import {
    WS_BATCH_LOT_UP_PROGRESS,
    BATCH_LOT_UP_ROW_BACKGROUND_COLOR_GREEN,
    BATCH_LOT_UP_ROW_BACKGROUND_COLOR_RED,
    BATCH_LOT_UP_ROW_BACKGROUND_COLOR_YELLOW
  } from "@/modules/constants";
  import SubmitButton from "@/core/components/common/SubmitButton";
  import {Formatter} from "@/core/services/formatter.service";

  const {mapActions, mapState} = createNamespacedHelpers('sales');
  const {mapState: globalMapState, mapActions: mapActionsGlobal} = createNamespacedHelpers('global');

  export default {
    name: "BatchLotUp",
    components: {SubmitButton, SortIcon, LiveSaleRef, BatchLotUpImageTd, ImageViewer, Spinner, draggable},
    data() {
      return {
        reservedItemsMap: [],
        lotItemsMap: [],
        currentGroupBy: 'category',
        smallestLotNo: null,
        options: [
          {text: this.$i18n.t('Category'), value: 'category'},
          {text: this.$i18n.t('Receipt'), value: 'receipt'},
          {text: this.$i18n.t('Item Location'), value: 'item-location'},
          {text: this.$i18n.t('Customer'), value: 'customer'},
          {text: this.$i18n.t('Uncategorized'), value: 'uncategorized'},
        ],
        currentView: 'thumbnail',
        viewOptions: [
          {text: this.$i18n.t('Thumbnail'), value: 'thumbnail'},
          {text: this.$i18n.t('List'), value: 'list'},
        ],
        blockingOptions: [
          {value: true},
          {value: false},
        ],
        blockingEnabled: true,
        progress: null,
        disableSubmit: false
      }
    },
    computed: {
      ...mapState({
        loading: state => state.batchLotUpData.loading,
        sale: state => state.batchLotUpData.sale,
        items: state => state.batchLotUpData.items,
        reservedLotNos: state => state.batchLotUpData.reservedLotNos,
        lotNosWithLiveId: state => state.batchLotUpData.lotNosWithLiveId,
      }),
      ...globalMapState({
        enableInventoryQuantity: state => state.enableInventoryQuantity
      }),
      thumbnailView() {
        return this.currentView === 'thumbnail'
      },
      isInProgress() {
        return this.progress !== null
      }
    },
    methods: {
      ...mapActionsGlobal(['addProgress', 'removeProgress', 'updateProgressValue']),
      ...mapActions(['initBatchLotUpData']),
      async transfer(fromArr, toArr) {
        for (let outerData of fromArr) {
          let items = _.remove(outerData.items, (el) => el.checked);

          if (items.length === 0) {
            continue;
          }

          let mappedObj = toArr.find(i => i.uuid === outerData.uuid);
          if (!mappedObj) {
            toArr.push({name: outerData.name, uuid: outerData.uuid, checked: false, orderDesc: null, items: []});
            mappedObj = toArr[toArr.length - 1];
          }
          mappedObj.items.push(...items.map(item => {
            item.checked = false;
            return item;
          }));
        }
      },
      async onMoveRightClick() {
        await this.transfer(this.reservedItemsMap, this.lotItemsMap);
        this.reservedItemsMap = [...this.reservedItemsMap.filter(i => i.items.length > 0)];
        this.lotItemsMap.forEach(outerData => {
          if (outerData.orderDesc !== null) {
            outerData.items.sort(this.compareStockNos(outerData.orderDesc))
          }
        })
        this.recalculateLotNos();
      },
      onMoveLeftClick() {
        this.transfer(this.lotItemsMap, this.reservedItemsMap);
        this.lotItemsMap = [...this.lotItemsMap.filter(i => i.items.length > 0)];
        this.recalculateLotNos();
        this.reservedItemsMap.map(outer => outer.items.map(i => i.lot_no = null));
      },
      selectAllRows(checked, outerData) {
        outerData.checked = checked;
        outerData.items.forEach((item) => {
          if (!(this.blockingEnabled && item.lot && item.lot.live_id)) {
            return item.checked = checked
          }
        });
      },
      selectRow(checked, item, outerData) {
        item.checked = checked;
        let res = outerData.items.filter(item => item.checked);
        outerData.checked = res.length === outerData.items.length;
      },
      recalculateLotNos() {
        let tempLotNos = this.blockingEnabled ? this.lotNosWithLiveId : []
        let smallestLotNoNumeric = parseInt(this.smallestLotNo);
        let counter = smallestLotNoNumeric - 1;
        this.lotItemsMap.forEach((outerData) => {
          outerData.items = outerData.items.map((item) => {
            if (this.blockingEnabled && item.lot && item.lot.live_id) {
              return item
            } else {
              let nextCounter = counter;
              while (true) {
                nextCounter++
                if (!this.reservedLotNos.includes(nextCounter) && !tempLotNos.includes(nextCounter)) {
                  counter = nextCounter;
                  break;
                }
              }
              item.lot_no = parseInt(counter);
            }
            return item;
          });
        });
      },
      onOuterDataDrag() {
        this.recalculateLotNos();
      },
      onInnerItemDrag() {
        this.recalculateLotNos();
      },
      sortByStockNo(outerData) {
        if (outerData.orderDesc || outerData.orderDesc === null) {
          outerData.orderDesc = false;
        } else if (outerData.orderDesc === false) {
          outerData.orderDesc = true;
        }
        outerData.items.sort(this.compareStockNos(outerData.orderDesc))
        this.recalculateLotNos();
      },
      compareStockNos(orderDesc) {
        return (a, b) => {
          a = a.inventory.stock_no.split('/');
          b = b.inventory.stock_no.split('/');
          let returnValue = 1
          if (orderDesc) {
            returnValue = -1
          }
          if (a[0] < b[0]) {
            return -returnValue;
          }
          if (a[0] > b[0]) {
            return returnValue;
          }
          if (a[0] === b[0]) {
            if (parseInt(a[1]) < parseInt(b[1])) {
              return -returnValue;
            }
            if (parseInt(a[1]) > parseInt(b[1])) {
              return returnValue;
            }
          }
          return 0;
        }
      },
      initDataByCategory(items) {
        items.forEach((item) => {
          let sourceArr = item.lot_no ? this.lotItemsMap : this.reservedItemsMap;

          if ((!this.smallestLotNo && item.lot_no) || (this.smallestLotNo && item.lot_no && parseInt(this.smallestLotNo) > parseInt(item.lot_no))) {
            this.smallestLotNo = item.lot_no;
          }

          let mappedObj = sourceArr.find(i => i.uuid === item.inventory.category.uuid);
          if (!mappedObj) {
            sourceArr.push({
              name: Formatter.getByMasterLanguage(item.inventory.category.name),
              uuid: item.inventory.category.uuid,
              checked: false,
              orderDesc: null,
              items: []
            });
            mappedObj = sourceArr[sourceArr.length - 1];
          }
          mappedObj.items.push({...item, checked: false});
        });

        if (!this.smallestLotNo) {
          this.smallestLotNo = 1;
        }
      },
      initDataByReceipt(items) {
        items.forEach((item) => {
          let sourceArr = item.lot_no ? this.lotItemsMap : this.reservedItemsMap;

          if ((!this.smallestLotNo && item.lot_no) || (this.smallestLotNo && item.lot_no && parseInt(this.smallestLotNo) > parseInt(item.lot_no))) {
            this.smallestLotNo = item.lot_no;
          }

          let mappedObj = sourceArr.find(i => i.uuid === item.inventory.receipt.uuid);
          if (!mappedObj) {
            sourceArr.push({
              name: `${this.$i18n.t('Receipt')} ${item.inventory.receipt.receipt_no}`,
              uuid: item.inventory.receipt.uuid,
              checked: false,
              orderDesc: null,
              items: []
            });
            mappedObj = sourceArr[sourceArr.length - 1];
          }
          mappedObj.items.push({...item, checked: false});
        });
        if (!this.smallestLotNo) {
          this.smallestLotNo = 1;
        }
      },
      initDataByItemLocation(items) {
        items.forEach((item) => {
          let sourceArr = item.lot_no ? this.lotItemsMap : this.reservedItemsMap;

          if ((!this.smallestLotNo && item.lot_no) || (this.smallestLotNo && item.lot_no && parseInt(this.smallestLotNo) > parseInt(item.lot_no))) {
            this.smallestLotNo = item.lot_no;
          }

          let mappedObj = sourceArr.find(i => i.uuid === item.inventory.item_location.uuid);
          if (!mappedObj) {
            sourceArr.push({
              name: item.inventory.item_location.name,
              uuid: item.inventory.item_location.uuid,
              checked: false,
              orderDesc: null,
              items: []
            });
            mappedObj = sourceArr[sourceArr.length - 1];
          }
          mappedObj.items.push({...item, checked: false});
        });

        if (!this.smallestLotNo) {
          this.smallestLotNo = 1;
        }
      },
      initDataByCustomer(items) {
        items.forEach((item) => {
          let sourceArr = item.lot_no ? this.lotItemsMap : this.reservedItemsMap;

          if ((!this.smallestLotNo && item.lot_no) || (this.smallestLotNo && item.lot_no && parseInt(this.smallestLotNo) > parseInt(item.lot_no))) {
            this.smallestLotNo = item.lot_no;
          }

          let mappedObj = sourceArr.find(i => i.uuid === item.inventory.receipt.customer_uuid);
          if (!mappedObj) {
            sourceArr.push({
              name: item.inventory.receipt.customer.display_name,
              uuid: item.inventory.receipt.customer_uuid,
              checked: false,
              orderDesc: null,
              items: []
            });
            mappedObj = sourceArr[sourceArr.length - 1];
          }
          mappedObj.items.push({...item, checked: false});
        });

        if (!this.smallestLotNo) {
          this.smallestLotNo = 1;
        }
      },
      initDataByUncategorized(items) {
        items.forEach((item) => {
          let sourceArr = item.lot_no ? this.lotItemsMap : this.reservedItemsMap;

          if ((!this.smallestLotNo && item.lot_no) || (this.smallestLotNo && item.lot_no && parseInt(this.smallestLotNo) > parseInt(item.lot_no))) {
            this.smallestLotNo = item.lot_no;
          }
          let mappedObj = sourceArr[0]
          if (!mappedObj) {
            sourceArr.push({
              checked: false,
              orderDesc: null,
              items: []
            });
            mappedObj = sourceArr[sourceArr.length - 1];
          }
          mappedObj.items.push({...item, checked: false});
        });

        if (!this.smallestLotNo) {
          this.smallestLotNo = 1;
        }
      },
      onGroupByChange(val) {
        this.reservedItemsMap = [];
        this.lotItemsMap = [];

        if (val === 'receipt') {
          this.initDataByReceipt(_.cloneDeep(this.items));
        } else if (val === 'category') {
          this.initDataByCategory(_.cloneDeep(this.items));
        } else if (val === 'item-location') {
          this.initDataByItemLocation(_.cloneDeep(this.items));
        } else if (val === 'customer') {
          this.initDataByCustomer(_.cloneDeep(this.items));
        } else if (val === 'uncategorized') {
          this.initDataByUncategorized(_.cloneDeep(this.items));
        }
      },
      getImageSrc(images) {
        for (let img of images) {
          if (img.data && (img.data.thumb || img.data.original)) {
            return img.data.thumb ? img.data.thumb.url : img.data.original.url;
          }
        }
        return null;
      },
      showImageGallery(data) {
        this.$refs.imageViewer.show(data);
      },
      getBgColor(item) {
        if (item.lot_no && item.lot && item.lot.live_id) {
          return BATCH_LOT_UP_ROW_BACKGROUND_COLOR_GREEN
        } else if ((item.lot && !item.lot.live_id && item.lot_no) || (!item.lot && item.lot_no)) {
          return BATCH_LOT_UP_ROW_BACKGROUND_COLOR_YELLOW
        }
        return BATCH_LOT_UP_ROW_BACKGROUND_COLOR_RED
      },
      async addProgressIfNotExist() {
        if (!this.progress) {
          this.progress = await this.addProgress(
            this.$i18n.t("Batch lot up for {sale_no}", {sale_no: this.sale.sale_no})
          );
        }
        return true
      },
      removeProgressIfExist() {
        if (this.progress) {
          this.removeProgress(this.progress.uuid)
          this.progress = null;
        }
      },
      async onProgressNotificationReceive(data) {
        if (!data.auction_uuid || data.auction_uuid !== this.sale.uuid) {
          return
        }
        await this.addProgressIfNotExist()
        this.updateProgressValue({
          progressValue: parseInt(Math.round(data.successful / data.total * 100)),
          uuid: this.progress.uuid
        });

        if (data.is_finished) {
          if (!data.errors) {
            this.$toast('success', this.$i18n.t("Batch lot up has successfully finished"));
          }
          this.removeProgressIfExist();
          await this.initBatchLotUpData(this.$route.params.sale_no);
          this.onGroupByChange(this.currentGroupBy)
        }
        if (data.errors) {
          if (data.errors.non_field_errors) {
            this.$errorDialog(data.errors.non_field_errors[0])
          } else if (data.errors.errors) {
            const listItemsArr = [];
            data.errors.errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Validation Error'));
          } else {
            this.$errorDialog(this.$i18n.t("Something went wrong while running batch lot up. Please, try again later."))
          }
        }
      },
      async onSaveClick() {
        this.disableSubmit = true
        let requestData = [];
        this.lotItemsMap.forEach((outerData) => {
          requestData.push(...outerData.items.map((item) => {
            return {uuid: item.uuid, lot_no: item.lot_no}
          }))
        });
        let response = await SalesService.batchLotUp(this.sale.sale_no, requestData);
        await this.addProgressIfNotExist();
        if (response.success) {
          this.$toast('success', this.$i18n.t('Batch lot up has successfully started'))
        } else if (response.body.non_field_errors) {
          this.$errorDialog(response.body.non_field_errors[0])
        }
        this.disableSubmit = false
      },
    },
    async beforeMount() {
      await this.initBatchLotUpData(this.$route.params.sale_no);
      this.initDataByCategory(_.cloneDeep(this.items));
    },
    mounted() {
      this.$ws.listen(`sales.${WS_BATCH_LOT_UP_PROGRESS}`, this.onProgressNotificationReceive);
    },
    beforeDestroy() {
      this.removeProgressIfExist();
      this.$ws.remove(`sales.${WS_BATCH_LOT_UP_PROGRESS}`);
    }
  }
</script>

<style lang="scss" scoped>
  @import "./../scss/variables";

  .outer-card-body {

    min-height: 20em;
    height: 100%;
    overflow-y: auto;

    .card {
      margin-bottom: 1em;

      &.sortable-ghost {
        background-color: $secondary;
        border: 1.5px dashed #b9bec4;
      }

      .inner-items-card-body {
        padding-top: 0;
        padding-bottom: 0;

        tr:first-child {
          td {
            border-top: none;
          }
        }

        tr {
          &.sortable-ghost {
            background-color: $secondary;
            border: 1.5px dashed #b9bec4;
          }
        }

        th {
          border-top: 0;
        }

        th.stock-th, th.quantity-th {
          width: 6em;
        }

        th.lot-th, th.estimate-th {
          width: 5em
        }

        th.handler-th, th.checkbox-th {
          width: 2em
        }

        th.stock-sort {
          &:hover {
            cursor: pointer;
          }
        }
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .collapsed > .when-opened,
  :not(.collapsed) > .when-closed {
    display: none;
  }


  .flex-container {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-content: stretch;
    align-items: flex-start;
  }

  .flex-item:nth-child(1) {
    order: 0;
    flex: 6;
    align-self: auto;
  }

  .flex-item:nth-child(2) {
    order: 0;
    flex: 1 1 auto;
    align-self: center;
  }

  .flex-item:nth-child(3) {
    order: 0;
    flex: 6;
    align-self: auto;
  }

  .title {
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 150px;
    white-space: nowrap;
  }

  //radio buttons tooltips does not work on hover, but on click,
  //As a workaround added absolutely positioned div to show tooltip on radio button option hover
  .tooltip-shower {
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
  }
</style>

